import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRoles } from '../../contexts/UserRolesContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { getLegalFilesByLawyerID } from '../../services/legalFileService';
import { fetchClientsByRoleIDs } from '../../services/clientService';
import type { LegalFile, Client, Role, Payment } from '../../types';
import { SectionHeader, SectionSearchBar } from '../../components/ui';
import { Download, Building2, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';

// Import existing components
import ClientSelectionPanel from './ClientSelectionPanel';
import LegalFilesListPanel from './LegalFilesListPanel';
import FileDetailsModal from './FileDetailsModal';
import AddFileModal from './AddFileModal';
import ArchiveConfirmationModal from './ArchiveConfirmationModal';
import AttachmentViewModal from './AttachmentViewModal';
import StatusBadge from './StatusBadge';


const LegalFiles: React.FC = () => {
  const { user } = useAuth();
  const { roles } = useUserRoles();
  const { language, strings, isRTL } = useLocalization();
  const { presetData } = useSystemPreset();
  
  // Debug RTL values
  console.log('Language:', language, 'isRTL:', isRTL);
  
  // Core state
  const [files, setFiles] = useState<LegalFile[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedFile, setSelectedFile] = useState<LegalFile | null>(null);
  
  // Modal states
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [showFileDetailsModal, setShowFileDetailsModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showAttachmentViewModal, setShowAttachmentViewModal] = useState(false);
  const [fileToArchive, setFileToArchive] = useState<LegalFile | null>(null);
  const [viewingAttachment, setViewingAttachment] = useState<any>(null);
  
  // File details modal states
  const [activeTab, setActiveTab] = useState<'overview' | 'payments' | 'events' | 'attachments'>('overview');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  
  // Inline editing states
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<LegalFile>>({});
  
  // Attachment view states
  const [imageZoom, setImageZoom] = useState(100);
  
  // File creation states
  const [creatingFile, setCreatingFile] = useState(false);
  const [archivingFile, setArchivingFile] = useState(false);
  const [newFileData, setNewFileData] = useState({
    title: '',
    description: '',
    typeID: '',
    statusID: '',
    caseNumber: '',
    caseYear: '',
    courtTypeID: '',
    caseArea: '',
    caseFirstSessionDate: '',
    caseOpponents: '',
    caseTypeID: '',
    expectedFees: 0,
    totalIncome: 0,
    totalExpenses: 0,
    notes: ''
  });

  // Set first role as default when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && !selectedRole) {
      setSelectedRole(roles[0]);
    }
  }, [roles, selectedRole]);

  // Fetch clients when a role is selected
  useEffect(() => {
    const fetchClientsForRole = async (role: Role) => {
      try {
        setLoading(true);
        setError(null);

        // Fetch clients for the selected role
        const roleClients = await fetchClientsByRoleIDs([role.id]);
        
        setClients(roleClients);
        
        // Reset selected client when role changes
        setSelectedClient(null);
      } catch (err) {
        console.error('Error fetching clients for role:', err);
        setError('Failed to load clients. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (selectedRole) {
      fetchClientsForRole(selectedRole);
    }
  }, [selectedRole]);

  // Fetch legal files when a client is selected
  useEffect(() => {
    const fetchLegalFilesForClient = async () => {
      if (!selectedClient) {
        setFiles([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch all legal files for the lawyer, then filter by client
        const allLegalFiles = await getLegalFilesByLawyerID(user?.id || '');
        
        // Try both clientId and clientID fields
        const clientFiles = allLegalFiles.filter(file => {
          const matchesClientId = file.clientId === selectedClient.id;
          const matchesClientID = file.clientID === selectedClient.id;
          return matchesClientId || matchesClientID;
        });
        
        setFiles(clientFiles);
        
        // Auto-select first file if available
        if (clientFiles.length > 0) {
          setSelectedFile(clientFiles[0]);
        } else {
          setSelectedFile(null);
        }
      } catch (err) {
        console.error('Error fetching legal files for client:', err);
        setError('Failed to load legal files. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (selectedClient) {
      fetchLegalFilesForClient();
    }
  }, [selectedClient, user?.id]);

  const handleRoleChange = (role: Role) => {
    setSelectedRole(role);
    setSelectedClient(null);
  };

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setSelectedFile(null); // Clear selected file when client changes
  };


  // Modal handlers
  const handleAddNewFile = () => {
    setShowAddFileModal(true);
  };

  const handleRefresh = () => {
    if (selectedClient) {
      // Trigger the useEffect that loads files for the selected client
      setFiles([]);
      setLoading(true);
    }
  };

  const exportToExcel = () => {
    try {
      const exportData = files.map(file => ({
        'File ID': file.id,
        'Title': file.title || 'N/A',
        'Description': file.description || 'N/A',
        'Case Number': file.caseNumber || 'N/A',
        'Case Year': file.caseYear || 'N/A',
        'Case Area': 'N/A', // caseArea property doesn't exist in LegalFile type
        'Case First Session Date': file.caseFirstSessionDate || 'N/A',
        'Case Opponents': file.caseOpponents || 'N/A',
        'Expected Fees': file.expectedFees || 0,
        'Total Income': file.totalIncome || 0,
        'Total Expenses': 'N/A', // totalExpenses property doesn't exist in LegalFile type
        'Status': file.statusID ? presetData?.fileStatuses?.find(s => s.id === file.statusID)?.name_en || 'N/A' : 'N/A',
        'Type': file.typeID ? presetData?.fileTypes?.find(t => t.id === file.typeID)?.name_en || 'N/A' : 'N/A',
        'Court Type': file.courtTypeID ? presetData?.courtTypes?.find(c => c.id === file.courtTypeID)?.name_en || 'N/A' : 'N/A',
        'Case Type': file.caseTypeID ? presetData?.caseTypes?.find(c => c.id === file.caseTypeID)?.name_en || 'N/A' : 'N/A',
        'Notes': file.notes || 'N/A',
        'Created At': file.createdAt ? new Date(parseInt(file.createdAt)).toLocaleDateString() : 'N/A',
        'Updated At': file.updatedAt ? new Date(parseInt(file.updatedAt)).toLocaleDateString() : 'N/A',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'LegalFiles');
      XLSX.writeFile(wb, `legal_files_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting legal files to Excel:', err);
      setError(language === 'ar' ? 'فشل في تصدير الملفات القانونية' : 'Failed to export legal files to Excel.');
    }
  };

  const handleViewDetails = (file: LegalFile) => {
    setSelectedFile(file);
    setShowFileDetailsModal(true);
    loadFileEntities(file);
  };

  const handleEditFile = () => {
    if (selectedFile) {
      setEditData(selectedFile);
      setIsEditing(true);
    }
  };

  const handleArchiveFile = () => {
    if (selectedFile) {
      setFileToArchive(selectedFile);
      setShowArchiveModal(true);
    }
  };

  const handleCloseFileDetails = () => {
    setShowFileDetailsModal(false);
    setIsEditing(false);
    setEditData({});
  };

  const handleTabChange = (tab: 'overview' | 'payments' | 'events' | 'attachments') => {
    setActiveTab(tab);
  };

  // File creation handlers
  const handleInputChange = (field: string, value: any) => {
    setNewFileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveFile = async () => {
    if (!selectedClient || !user || !selectedRole) return;
    
    setCreatingFile(true);
    try {
      // Create new file logic here
      console.log('Creating new file:', newFileData);
      // Reset form
      setNewFileData({
        title: '',
        description: '',
        typeID: '',
        statusID: '',
        caseNumber: '',
        caseYear: '',
        courtTypeID: '',
        caseArea: '',
        caseFirstSessionDate: '',
        caseOpponents: '',
        caseTypeID: '',
        expectedFees: 0,
        totalIncome: 0,
        totalExpenses: 0,
        notes: ''
      });
      setShowAddFileModal(false);
    } catch (error) {
      console.error('Error creating file:', error);
    } finally {
      setCreatingFile(false);
    }
  };

  // Load file entities (payments, events, attachments)
  const loadFileEntities = async (file: LegalFile) => {
    setLoadingEntities(true);
    try {
      // Load payments, events, and attachments for the selected file
      // This would typically involve API calls
      console.log('Loading entities for file:', file.id);
      // Mock data for now
      setPayments([]);
      setEvents([]);
      setAttachments([]);
    } catch (error) {
      console.error('Error loading file entities:', error);
    } finally {
      setLoadingEntities(false);
    }
  };

  // Attachment handlers
  const handleViewAttachment = (attachment: any) => {
    setViewingAttachment(attachment);
    setShowAttachmentViewModal(true);
  };

  const handleDownloadAttachment = (attachment: any) => {
    // Download logic here
    console.log('Downloading attachment:', attachment.id);
  };

  const handleAddAttachment = () => {
    // This will be handled by the FileDetailsModal
    console.log('Adding attachment');
  };

  const handleUploadAttachment = async (attachmentData: any) => {
    // Upload logic here
    console.log('Uploading attachment:', attachmentData);
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    // Delete logic here
    console.log('Deleting attachment:', attachmentId);
  };

  // Payment handlers
  const handleAddPayment = (paymentData: Payment) => {
    setPayments(prev => [...prev, paymentData]);
  };

  const handleDeletePayment = async (paymentId: string) => {
    setPayments(prev => prev.filter(p => p.id !== paymentId));
  };

  // Archive handlers
  const handleConfirmArchive = async () => {
    if (!fileToArchive) return;
    
    setArchivingFile(true);
    try {
      // Archive/unarchive logic here
      console.log('Archiving file:', fileToArchive.id);
      setShowArchiveModal(false);
      setFileToArchive(null);
    } catch (error) {
      console.error('Error archiving file:', error);
    } finally {
      setArchivingFile(false);
    }
  };

  // Inline editing handlers
  const handleSaveEdit = () => {
    // Save edit logic here
    console.log('Saving edit:', editData);
    setIsEditing(false);
    setEditData({});
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
  };

  // Zoom handlers
  const handleZoomIn = () => setImageZoom(prev => Math.min(prev + 25, 300));
  const handleZoomOut = () => setImageZoom(prev => Math.max(prev - 25, 25));
  const handleResetZoom = () => setImageZoom(100);

  // Helper functions for preset data
  const getFileTypeName = (typeID: string, lang: 'en' | 'ar') => {
    const fileType = presetData?.fileTypes?.find((type: any) => type.id === typeID);
    return fileType ? fileType[lang === 'ar' ? 'name_ar' : 'name_en'] : 'Unknown Type';
  };

  const getFileStatusName = (statusID: string, lang: 'en' | 'ar') => {
    const fileStatus = presetData?.fileStatuses?.find((status: any) => status.id === statusID);
    return fileStatus ? fileStatus[lang === 'ar' ? 'name_ar' : 'name_en'] : 'Unknown Status';
  };

  const getCourtTypeName = (courtTypeID: string, lang: 'en' | 'ar') => {
    const courtType = presetData?.courtTypes?.find((type: any) => type.id === courtTypeID);
    return courtType ? courtType[lang === 'ar' ? 'name_ar' : 'name_en'] : 'Unknown Court';
  };

  const getCaseTypeName = (caseTypeID: string, lang: 'en' | 'ar') => {
    const caseType = presetData?.caseTypes?.find((type: any) => type.id === caseTypeID);
    return caseType ? caseType[lang === 'ar' ? 'name_ar' : 'name_en'] : 'Unknown Case Type';
  };

  const getAttachmentTypeName = (type: string, lang: 'en' | 'ar') => {
    const attachmentType = presetData?.attachmentTypes?.find((t: any) => t.id === type);
    return attachmentType ? attachmentType[lang === 'ar' ? 'name_ar' : 'name_en'] : 'Unknown Type';
  };

  const getAttachmentTypeIcon = (type: string) => {
    const attachmentType = presetData?.attachmentTypes?.find((t: any) => t.id === type);
    return (attachmentType as any)?.icon || '📎';
  };

  const getMimeTypeFromAttachmentType = (type: string) => {
    const attachmentType = presetData?.attachmentTypes?.find((t: any) => t.id === type);
    return (attachmentType as any)?.mimeType || 'application/octet-stream';
  };

  const getPaymentTypeLabel = (paymentTypeId: string) => {
    return paymentTypeId === '1' ? 'Income' : 'Expense';
  };

  const getLawyerTitleName = (titleId: string, lang: 'en' | 'ar') => {
    const title = presetData?.lawyerTitles?.find((t: any) => t.id === titleId);
    return title ? title[lang === 'ar' ? 'name_ar' : 'name_en'] : 'Legal Professional';
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB');
  };

  const getTypeLabel = (typeID: string) => {
    return getFileTypeName(typeID, language as 'en' | 'ar');
  };

  const getStatusBadge = (statusID: string) => {
    const statusName = getFileStatusName(statusID, language as 'en' | 'ar');
    return <StatusBadge statusName={statusName} language={language as 'en' | 'ar'} />;
  };

  const filteredFiles = files.filter(file => {
    const matchesSearch = file.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (file.description && file.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (file.caseNumber && file.caseNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (file.clientName && file.clientName.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'All' || getFileStatusName(file.statusID || '', language as 'en' | 'ar') === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">Loading Legal Files</h2>
          <p className="text-slate-600 dark:text-slate-300">Fetching your legal files...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 dark:text-red-400 text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">Error Loading Files</h2>
          <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader
          title={strings.legalFiles?.title || 'Legal Files'}
          description={strings.legalFiles?.subtitle || 'Manage your legal documents and case files'}
          language={language}
          isRTL={isRTL}
          actionButtons={[
            {
              label: language === 'ar' ? 'تصدير إكسل' : 'Export Excel',
              onClick: exportToExcel,
              icon: <Download className="w-4 h-4" />,
              variant: 'secondary',
              requiresProfessional: false
            }
          ]}
          refreshButton={{
            onClick: handleRefresh,
            disabled: !selectedRole,
            loading: loading
          }}
          addButton={{
            label: strings.legalFiles?.addNewFile || 'Add New File',
            onClick: handleAddNewFile
          }}
        >
          {/* Roles Picker */}
          {roles.length > 0 && (
            <div className="mb-6">
              <label className={`block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                {language === 'ar' ? 'اختر دور المكتب' : 'Select Office Role'}
              </label>
              <div className={`flex flex-wrap gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleRoleChange(role)}
                    className={`px-4 py-3 rounded-xl border-2 transition-all duration-300 flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} ${
                      selectedRole?.id === role.id
                        ? 'border-primary bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 shadow-lg'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md'
                    }`}
                  >
                    <Building2 className="w-5 h-5" />
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <div className="font-semibold">{role.officeName}</div>
                      <div className="text-sm opacity-75">{role.lawyerTitleID ? getLawyerTitleName(role.lawyerTitleID, language as 'en' | 'ar') : 'Legal Professional'}</div>
                    </div>
                    {selectedRole?.id === role.id && (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search and Status Filter */}
          {selectedClient && (
            <div className={`flex flex-col sm:flex-row gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <div className="flex-1">
                <SectionSearchBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onClearSearch={() => setSearchQuery('')}
                  placeholder={language === 'ar' ? 'البحث في الملفات...' : 'Search files...'}
                  language={language}
                  isRTL={isRTL}
                />
              </div>
              <div className="sm:w-48">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className={`w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                    isRTL ? 'text-right' : 'text-left'
                  }`}
                  style={{
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                >
                  <option value="All">
                    {language === 'ar' ? 'جميع الحالات' : 'All Status'}
                  </option>
                  <option value="Active">
                    {language === 'ar' ? 'نشط' : 'Active'}
                  </option>
                  <option value="Closed">
                    {language === 'ar' ? 'مغلق' : 'Closed'}
                  </option>
                  <option value="Archived">
                    {language === 'ar' ? 'مؤرشف' : 'Archived'}
                  </option>
                </select>
              </div>
            </div>
          )}
        </SectionHeader>

        {/* Two Panel Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Client Selection Panel */}
          <div className={`lg:w-1/2 ${isRTL ? 'lg:order-2' : 'lg:order-1'}`}>
            <ClientSelectionPanel
              clients={clients}
              selectedClient={selectedClient}
              selectedRole={selectedRole}
              loading={loading}
              language={language as 'en' | 'ar'}
              strings={strings}
              onClientSelect={handleClientSelect}
            />
          </div>

          {/* Legal Files List Panel */}
          <div className={`lg:w-1/2 ${isRTL ? 'lg:order-1' : 'lg:order-2'}`}>
            <LegalFilesListPanel
              legalFiles={files}
              filteredFiles={filteredFiles}
              selectedClient={selectedClient}
              selectedFile={selectedFile}
              loading={loading}
              searchQuery={searchQuery}
              filterStatus={filterStatus}
              language={language as 'en' | 'ar'}
              strings={strings}
              getTypeLabel={getTypeLabel}
              getStatusBadge={getStatusBadge}
              onViewDetails={handleViewDetails}
            />
          </div>
        </div>
                          
        {/* Modals */}
        {showAddFileModal && selectedClient && (
          <AddFileModal
            showAddFileModal={showAddFileModal}
            creatingFile={creatingFile}
            newFileData={newFileData}
            selectedClient={selectedClient}
            language={language as 'en' | 'ar'}
            strings={strings}
            presetData={presetData}
            getFileTypeName={getFileTypeName}
            getFileStatusName={getFileStatusName}
            getCourtTypeName={getCourtTypeName}
            getCaseTypeName={getCaseTypeName}
            onClose={() => setShowAddFileModal(false)}
            onInputChange={handleInputChange}
            onSave={handleSaveFile}
          />
        )}

        {showFileDetailsModal && selectedFile && (
          <FileDetailsModal
            selectedFile={selectedFile}
            activeTab={activeTab}
            payments={payments}
            events={events}
            attachments={attachments}
            loadingEntities={loadingEntities}
            language={language as 'en' | 'ar'}
            strings={strings}
            getTypeLabel={getTypeLabel}
            getFileTypeName={getFileTypeName}
            getFileStatusName={getFileStatusName}
            getCourtTypeName={getCourtTypeName}
            getCaseTypeName={getCaseTypeName}
            getAttachmentTypeName={getAttachmentTypeName}
            getAttachmentTypeIcon={getAttachmentTypeIcon}
            getMimeTypeFromAttachmentType={getMimeTypeFromAttachmentType}
            getPaymentTypeLabel={getPaymentTypeLabel}
            formatDate={formatDate}
            onClose={handleCloseFileDetails}
            onTabChange={handleTabChange}
            onEditFile={handleEditFile}
            onArchiveFile={handleArchiveFile}
            onViewAttachment={handleViewAttachment}
            onDownloadAttachment={handleDownloadAttachment}
            onAddAttachment={handleAddAttachment}
            onUploadAttachment={handleUploadAttachment}
            onAddPayment={handleAddPayment}
            onDeletePayment={handleDeletePayment}
            onDeleteAttachment={handleDeleteAttachment}
            user={user}
            selectedRole={selectedRole}
            isEditing={isEditing}
            editData={editData}
            setEditData={setEditData}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            presetData={presetData}
          />
        )}

        {showArchiveModal && fileToArchive && (
          <ArchiveConfirmationModal
            showArchiveModal={showArchiveModal}
            fileToArchive={fileToArchive}
            archivingFile={archivingFile}
            language={language as 'en' | 'ar'}
            strings={strings}
            onCancel={() => setShowArchiveModal(false)}
            onConfirm={handleConfirmArchive}
          />
        )}

        {showAttachmentViewModal && viewingAttachment && (
          <AttachmentViewModal
            viewingAttachment={viewingAttachment}
            imageZoom={imageZoom}
            language={language as 'en' | 'ar'}
            getAttachmentTypeName={getAttachmentTypeName}
            getAttachmentTypeIcon={getAttachmentTypeIcon}
            getMimeTypeFromAttachmentType={getMimeTypeFromAttachmentType}
            formatDate={formatDate}
            onClose={() => setShowAttachmentViewModal(false)}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onResetZoom={handleResetZoom}
            onDownloadAttachment={handleDownloadAttachment}
            onDeleteAttachment={handleDeleteAttachment}
          />
        )}
      </div>
    </div>
  );
};

export default LegalFiles;